import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CalendarClock } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { Soft } from "../../Common/Soft";
import { tokens } from "@/theme/tokens";
import { entryVariants, resolveTransition } from "../motion";
import type { PendingReview } from "../../../chat/hooks/useStreamingTurn";
import { APPROVAL_TTL_MS } from "./EmailApprovalCard";

export interface TimesheetApprovalCardProps {
  review: PendingReview;
  onDecide: (decision: "approve" | "reject") => void | Promise<void>;
}

/** Same poll as the email card: wall-clock arithmetic, so a laptop that slept
 *  through the hour catches up on wake whether or not a timer fired. */
const EXPIRY_TICK_MS = 15_000;

/** Tool names this card can describe, vs. ones it renders generically. */
const BULK_TOOL = "save_timesheet_entries_bulk";

/** The tool's own name, with any `<plugin>__<server>__` catalog prefix dropped. */
function upstreamName(toolName: string): string {
  const i = toolName.lastIndexOf("__");
  return i === -1 ? toolName : toolName.slice(i + 2);
}

interface Framing {
  title: string;
  /** Short label/value pairs describing what the call will act on. */
  facts: Array<[string, string]>;
  /** The consequence a reader needs BEFORE clicking, not after. */
  warning?: string;
  confirmLabel: string;
}

/**
 * How each gated timesheet tool should ask its question.
 *
 * One generic title for all of them would be a worse card than none: "Approve
 * this timesheet action?" over a submit reads as routine, and submitting
 * freezes a payroll week. The question has to name what approving does.
 */
function framingFor(tool: string, args: Record<string, unknown> | null): Framing {
  const get = (k: string) => (args && args[k] != null ? String(args[k]) : "");
  const week = get("week");
  const emp = get("empnum");
  const facts: Array<[string, string]> = [];
  if (week) facts.push(["Week of", week]);
  if (emp) facts.push(["Employee", emp]);

  switch (tool) {
    case "submit_timesheet":
      return {
        title: "Submit this timesheet?",
        facts,
        warning:
          "Submitting freezes the week for payroll. You cannot edit it afterwards without unsubmitting.",
        confirmLabel: "Submit timesheet",
      };
    case "unsubmit_timesheet":
      return { title: "Reopen this timesheet?", facts, confirmLabel: "Unsubmit" };
    case "approve_timesheet":
      return {
        title: "Approve this timesheet?",
        facts,
        warning: "Approving locks the week for the employee.",
        confirmLabel: "Approve timesheet",
      };
    case "unlock_timesheet":
      return { title: "Unlock this timesheet week?", facts, confirmLabel: "Unlock" };
    case "add_timesheet_comment":
      return {
        title: "Add this comment?",
        facts: [...facts, ...(get("comment") ? ([["Comment", get("comment")]] as Array<[string, string]>) : [])],
        confirmLabel: "Add comment",
      };
    case "delete_timesheet_entry":
      return {
        title: "Delete this timesheet entry?",
        facts: get("id") ? [["Entry", `#${get("id")}`]] : [],
        warning: "This removes a logged line. The hours go with it.",
        confirmLabel: "Delete entry",
      };
    case "create_timesheet_entry":
    case "update_timesheet_entry": {
      const rows: Array<[string, string]> = [];
      if (get("report_date")) rows.push(["Date", get("report_date")]);
      if (get("costcode")) rows.push(["Cost code", get("costcode")]);
      if (get("project")) rows.push(["Project", get("project")]);
      if (get("hours") || get("totalhours")) rows.push(["Hours", get("hours") || get("totalhours")]);
      return {
        title: tool === "create_timesheet_entry" ? "Add this timesheet entry?" : "Change this timesheet entry?",
        facts: get("id") ? [["Entry", `#${get("id")}`], ...rows] : rows,
        confirmLabel: "Save entry",
      };
    }
    default:
      return { title: "Approve this timesheet action?", facts, confirmLabel: "Approve" };
  }
}

interface Row {
  date: string;
  project: string;
  code: string;
  hours: number;
  notes: string;
  op: "create" | "update";
  id?: number;
}

const num = (v: unknown): number => (typeof v === "number" ? v : Number(v) || 0);
const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));

/** Flatten the diff payload into display rows, keeping create/update distinct.
 *
 *  `hours` is what the model sent. The server lays those hours out into a
 *  shift and inserts the break AFTER approval, so the card deliberately shows
 *  hours rather than clock times: showing times here would be showing numbers
 *  nobody has computed yet, and they would not be the ones written. */
function rowsOf(args: Record<string, unknown> | null): Row[] {
  const out: Row[] = [];
  for (const op of ["create", "update"] as const) {
    const list = args?.[op];
    if (!Array.isArray(list)) continue;
    for (const e of list) {
      if (!e || typeof e !== "object") continue;
      const r = e as Record<string, unknown>;
      out.push({
        date: str(r.report_date),
        project: r.project ? str(r.project) : "",
        code: str(r.costcode),
        hours: num(r.hours ?? r.totalhours),
        notes: str(r.notes),
        op,
        id: r.id == null ? undefined : num(r.id),
      });
    }
  }
  return out;
}

function deletionsOf(args: Record<string, unknown> | null): number[] {
  const d = args?.delete;
  return Array.isArray(d) ? d.map(num).filter((n) => n > 0) : [];
}

/** Group by date, preserving the order days first appear. */
function byDay(rows: Row[]): Array<[string, Row[]]> {
  const map = new Map<string, Row[]>();
  for (const r of rows) {
    const k = r.date || "(no date)";
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(r);
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
}

const fmtHours = (h: number) => (Number.isInteger(h) ? `${h}h` : `${h}h`);

const dayLabel = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};

/**
 * The approval gate for a gated timesheet write.
 *
 * This exists because gating anything other than `send_email` previously
 * rendered an EMAIL-shaped frame around it — `MessageList` had exactly one
 * card. A timesheet write announced as "Send this email?" is worse than no
 * card: it asks for consent to the wrong thing.
 *
 * Its job is that **the card and the write agree**. For a bulk entry that
 * means every line the user is about to commit, grouped by day with day and
 * week totals, because the whole point of the feature is reviewing a week at a
 * glance. A wall of text would be approved blind, which turns "tedious but
 * correct" into "fast and wrong" — strictly worse than entering by hand.
 *
 * APPROVE AND REJECT ONLY, for the same reason as the email card:
 * `effective_args` runs the PERSISTED arguments and the server refuses `edit`
 * outright (`approval_gate.py` `_EDIT_REFUSED_TEXT`), so an editable card
 * could show one timesheet and write another. Corrections go back through
 * conversation.
 */
export function TimesheetApprovalCard({ review, onDecide }: TimesheetApprovalCardProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<"idle" | "approved" | "rejected">("idle");
  const [shownAt] = useState(() => Date.now());
  const [timedOut, setTimedOut] = useState(false);
  const reduce = useReducedMotion() ?? false;

  useEffect(() => {
    if (timedOut) return;
    const id = setInterval(() => {
      if (Date.now() - shownAt >= APPROVAL_TTL_MS) setTimedOut(true);
    }, EXPIRY_TICK_MS);
    return () => clearInterval(id);
  }, [timedOut, shownAt]);

  const tool = upstreamName(review.tool_name);
  const isBulk = tool === BULK_TOOL;
  const framing = framingFor(tool, review.args);
  const rows = isBulk ? rowsOf(review.args) : [];
  const deletions = isBulk ? deletionsOf(review.args) : [];
  const days = byDay(rows);
  const weekTotal = rows.reduce((t, r) => t + r.hours, 0);

  const expired = timedOut && outcome === "idle";
  // The gate failed CLOSED: `error` arrived instead of `args`, the tool never
  // ran, and a decision would be written where nothing reads it.
  const gateFailed = Boolean(review.error);
  const disabled = busy || expired || gateFailed;

  const decide = (decision: "approve" | "reject") => async () => {
    if (disabled) return;
    setBusy(true);
    setError(null);
    try {
      await onDecide(decision);
      setOutcome(decision === "approve" ? "approved" : "rejected");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not record your decision");
    } finally {
      setBusy(false);
    }
  };

  const title = isBulk ? "Save these timesheet entries?" : framing.title;

  return (
    <motion.div initial="hidden" animate="visible" variants={entryVariants} transition={resolveTransition(reduce)}>
      <Soft
        variant="raised"
        role="region"
        aria-label="Timesheet approval"
        sx={{
          my: 1.5,
          p: 1.5,
          boxShadow: (t) =>
            `${t.palette.mode === "light" ? tokens.neu.light.raised : tokens.neu.dark.raised}, inset 0 0 0 1px ${alpha(t.palette.warning.main, 0.45)}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1, color: "warning.main" }}>
          <CalendarClock size={16} aria-hidden />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
        </Box>

        {days.map(([date, dayRows]) => {
          const dayTotal = dayRows.reduce((t, r) => t + r.hours, 0);
          return (
            <Box key={date} sx={{ mb: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                  {dayLabel(date)}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {fmtHours(dayTotal)}
                </Typography>
              </Box>
              {dayRows.map((r, i) => (
                <Box
                  key={`${date}-${i}`}
                  sx={{ display: "flex", gap: 1, alignItems: "baseline", pl: 1, py: 0.25 }}
                >
                  <Typography variant="body2" sx={{ minWidth: 48, color: "text.primary" }}>
                    {fmtHours(r.hours)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.primary", wordBreak: "break-word" }}>
                    {r.project ? `${r.project} · ` : ""}
                    {r.code}
                    {r.notes ? ` — ${r.notes}` : ""}
                  </Typography>
                  {/* An update REPLACES an existing line. Saying so is the
                      difference between adding an hour and overwriting one. */}
                  {r.op === "update" && (
                    <Typography variant="caption" sx={{ color: "info.main" }}>
                      {`replaces entry ${r.id ?? "?"}`}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          );
        })}

        {deletions.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "error.main" }}>
              {deletions.length === 1 ? "Removing 1 entry" : `Removing ${deletions.length} entries`}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", pl: 1 }}>
              {deletions.map((id) => `#${id}`).join(", ")}
            </Typography>
          </Box>
        )}

        {rows.length > 0 && (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                borderTop: (t) => `1px solid ${alpha(t.palette.text.primary, 0.12)}`,
                pt: 0.75,
                mt: 0.5,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Total
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {fmtHours(weekTotal)}
              </Typography>
            </Box>
            {/* Clock times are computed server-side after approval, so they are
                deliberately not shown: they would be numbers nobody has
                calculated yet. */}
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
              Start and end times, and the required break, are filled in when these are saved.
            </Typography>
          </>
        )}

        {/* A single-action write: name what it will act on, and say the
            consequence before the click rather than after. */}
        {!isBulk &&
          framing.facts.map(([label, value]) => (
            <Box key={label} sx={{ display: "flex", gap: 1, py: 0.25 }}>
              <Typography variant="body2" sx={{ color: "text.secondary", minWidth: 84 }}>
                {label}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.primary", wordBreak: "break-word" }}>
                {value}
              </Typography>
            </Box>
          ))}

        {!isBulk && framing.warning && (
          <Typography variant="body2" sx={{ color: "warning.main", mt: 0.5, mb: 0.5 }}>
            {framing.warning}
          </Typography>
        )}

        {/* Nothing the card can describe — an unrecognised gated tool, or a gate
            that failed before capturing arguments. Fall back to the server's own
            wording rather than an empty card. */}
        {rows.length === 0 && deletions.length === 0 && framing.facts.length === 0 && review.summary && (
          <Typography variant="body2" sx={{ color: "text.primary", mb: 0.5 }}>
            {review.summary}
          </Typography>
        )}

        {gateFailed && (
          <Typography variant="body2" sx={{ color: "error.main", mb: 1 }}>
            {`This approval could not be set up (${review.error}). Nothing was saved — ask again.`}
          </Typography>
        )}

        {expired && (
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            This approval expired and the server has already discarded it. Nothing was saved —
            ask again if you still want these entries.
          </Typography>
        )}

        {outcome === "idle" ? (
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <Button size="small" variant="contained" disabled={disabled} onClick={decide("approve")}>
              {isBulk ? "Save entries" : framing.confirmLabel}
            </Button>
            <Button size="small" variant="outlined" color="error" disabled={disabled} onClick={decide("reject")}>
              {isBulk ? "Don't save" : "Cancel"}
            </Button>
          </Box>
        ) : (
          <Typography
            variant="caption"
            sx={{ color: outcome === "approved" ? "success.main" : "text.secondary", mt: 1, display: "block" }}
          >
            {outcome === "approved" ? "Approved — saving…" : "Not saved."}
          </Typography>
        )}

        {error && (
          <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </Soft>
    </motion.div>
  );
}
